/**
 * ViewPromptButton — system_owner only.
 *
 * Renders a "View prompt" button in the output pane header. Clicking it
 * fetches GET /api/runs/:id/prompt and shows the assembled prompt in a
 * scrollable monospaced modal — useful for debugging failed runs where the
 * output is empty and the `error` text alone isn't enough.
 */
import { Box, Button, Modal, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { runsApi } from '../../api/endpoints/runs';

interface ViewPromptButtonProps {
  runId: string;
}

export function ViewPromptButton({ runId }: ViewPromptButtonProps) {
  const [opened, setOpened] = useState(false);
  const [loading, setLoading] = useState(false);
  const [prompt, setPrompt] = useState<string | null>(null);
  const [capturedAt, setCapturedAt] = useState<string | null>(null);

  async function handleOpen() {
    setOpened(true);
    if (prompt !== null) return; // already loaded
    setLoading(true);
    try {
      const res = await runsApi.getPrompt(runId);
      setPrompt(res.prompt);
      setCapturedAt(res.capturedAt);
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Failed to load prompt',
        message: err instanceof Error ? err.message : 'Unknown error',
      });
      setOpened(false);
    } finally {
      setLoading(false);
    }
  }

  const promptBytes = prompt?.length ?? 0;

  return (
    <>
      <Button
        size="xs"
        variant="default"
        onClick={() => void handleOpen()}
        loading={loading}
        data-testid="view-prompt-btn"
      >
        View prompt
      </Button>

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        title={
          <Text fw={600}>
            Prompt sent to OpenAI{prompt !== null ? ` (${promptBytes.toLocaleString()} chars)` : ''}
          </Text>
        }
        size="xl"
        data-testid="view-prompt-modal"
      >
        {prompt === null ? (
          <Text c="dimmed">
            (not captured — this run pre-dates the prompt-capture column, or generation never
            reached the OpenAI call)
          </Text>
        ) : (
          <>
            {capturedAt && (
              <Text size="xs" c="dimmed" mb="xs">
                Captured at run creation ({new Date(capturedAt).toLocaleString()}).
              </Text>
            )}
            <Box
              component="pre"
              p="sm"
              style={{
                background: 'var(--mantine-color-gray-0)',
                border: '1px solid var(--mantine-color-gray-2)',
                borderRadius: 4,
                maxHeight: '60vh',
                overflow: 'auto',
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                margin: 0,
              }}
              data-testid="view-prompt-body"
            >
              {prompt}
            </Box>
          </>
        )}
      </Modal>
    </>
  );
}
